import {
  Button,
  Card,
  DELETE_WARNING,
  Grid,
  Typography,
  newToast,
  useElementBreakpoints,
} from "@compass/core";
import {
  ApplicationResponseFile,
  FileTypeCategory,
  Permission,
  PermissionAction,
  StudentApplicationEditResource,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import { useRouter } from "hooks";
import { FileListItem } from "./FileList/FileListItem";
import { fileDetailPageRoute } from "./FileDetailPage/FileDetailPage";

export const StudentApplicationFiles = (props: { initialLoad: boolean; applicationResponseId: number; canEdit: boolean }) => {
  const ROOT_PERMISSION: Permission = Permission.StudentApplication;
  const { breakpointsRef } = useElementBreakpoints();
  const [applicationFileResource, setApplicationFileResource] = useState<StudentApplicationEditResource>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();
  const [files, setFiles] = useState<ApplicationResponseFile[]>();
  const [deletingFileId, setDeletingFileId] = useState<number>();

  let location = useLocation();

  const fetchList = () => {
    ApiService.studentApplications.getApplicationResponseFiles(props.applicationResponseId).then((response) => {
      setFiles(response);
    });

    ApiService.studentApplications.getFileEditResource()
      .then((response) => {
        setApplicationFileResource(response);
      });
  };

  useEffect(() => {
    if (!location.pathname.includes("dialog")) {
      fetchList();
    }
  }, [location]);

  useEffect(() => {
    if (props.initialLoad) {
      fetchList();
    }
  }, [props.initialLoad]);

  const deleteFile = async (fileId: number) => {
    if (!props.canEdit || !isAuthorized(ROOT_PERMISSION, PermissionAction.Delete) || deletingFileId != null) return;
    if (!window.confirm(DELETE_WARNING)) return;

    setDeletingFileId(fileId);
    try {
      await ApiService.studentApplications.deleteFile(fileId);
      setFiles((currentFiles) => currentFiles?.filter((file) => file.id !== fileId));
      dispatch(setAppToast(newToast("success")));
    } catch (ex) {
      dispatch(setAppToast(newToast("error")));
    } finally {
      setDeletingFileId(undefined);
    }
  };

  const renderFileItem = (file: ApplicationResponseFile, fileIndex: number) => {
    const matchedFileCategory = applicationFileResource?.fileCategories?.find(c => c.id == file.fileCategoryId);

    return (
      <FileListItem
        key={file.id}
        file={file}
        index={fileIndex}
        fileCategory={matchedFileCategory ? matchedFileCategory.name : undefined}
        onDelete={props.canEdit && isAuthorized(ROOT_PERMISSION, PermissionAction.Delete) ? () => deleteFile(file.id) : undefined}
        deleting={deletingFileId != null}
      />
    );
  };

  return (
    <>
      <Card header="Files">
        <Grid ref={breakpointsRef} container spacing="sm">
          {files?.length === 0 && (
            <Grid item xs={12}>
              <Typography>No Files</Typography>
            </Grid>
          )}
          {files?.map((file, fileIndex) => renderFileItem(file, fileIndex))}

          {props.canEdit && isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<FileUploadRoundedIcon />}
                onClick={() =>
                  navigate({
                    pathname: fileDetailPageRoute.getPath({
                      fileType: FileTypeCategory.Application,
                      screenType: "dialog",
                      crudMode: "edit",
                      parentEntityId: props.applicationResponseId,
                      entityId: 0,
                    }),
                  })
                }
              >
                Add File
              </Button>
            </Grid>
          )}
        </Grid>
      </Card>

      {props.canEdit && isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && matchTemplatePath(fileDetailPageRoute.templatePath) && <Outlet />}
    </>
  );
};
